import { AdvancedChips } from '@platform/engine/list/advanced/AdvancedChips';
import { AdvancedPanel } from '@platform/engine/list/advanced/AdvancedPanel';
import { useAdvancedDsl } from '@platform/engine/list/advanced/useAdvancedDsl';
import { useAdvancedPanel } from '@platform/engine/list/advanced/useAdvancedPanel';
import { hasActiveFilters, RESET_FILTERS } from '@platform/engine/list/filters/activeFilters';
import { FilterRow } from '@platform/engine/list/filters/FilterRow';
import { useSaveAsView } from '@platform/engine/list/savedViews/useSaveAsView';
import { ListToolbar } from '@platform/engine/list/toolbar/ListToolbar';
import { TableActions } from '@platform/engine/list/toolbar/TableActions';
import { useCanExport } from '@platform/engine/list/toolbar/useCanExport';
import { useCapability } from '@platform/engine/list/toolbar/useCapability';
import { useTranslation } from 'react-i18next';
import { DesignCreateMenu } from './DesignCreateMenu';
import type { IDesignListHeadProps } from './IDesignListHeadProps';

/**
 * Шапка списку проєктування під заголовком і вкладками розділу (прототип v4: список модуля бізнес-аналізу в групі «Проєктування»):
 * рядок фільтрів із вкладками подань, «Фільтр», «Розширені фільтри» і збереженим поданням; панель розширених фільтрів; панель дій
 * зі створенням запису аналізу (кілька видів — меню «+ Створити ▾», прототип v4 baNewMenu), діями над вибраними (редагування й копія — з
 * правом edit, вилучення — з правом delete: спостерігач і гість їх не бачать), експортом, друком, тегами й пошуком.
 */
export function DesignListHead({ model, actions, createText, views, createKinds = [] }: IDesignListHeadProps) {
  const { t } = useTranslation();
  const adv = useAdvancedPanel(model);
  const { clearAll, close } = adv;
  const saveAsView = useSaveAsView(model, useAdvancedDsl(model.ws, model.rt.key) ?? '', () => { clearAll(); close(); });
  const canExport = useCanExport(model.ws);
  const canCreate = useCapability(model.ws, 'edit');
  const canDelete = useCapability(model.ws, 'delete');
  return (
    <>
      <FilterRow model={model} table views={views} advanced={{ count: adv.applied, open: adv.open, onToggle: adv.toggle }}
        chips={<AdvancedChips ws={model.ws} rt={model.rt} conditions={adv.conditions} onChange={adv.change} quick={adv.quick} onQuick={adv.changeQuick} />} />
      {adv.open && (
        <AdvancedPanel model={model} conditions={adv.conditions} onChange={adv.change} quick={adv.quick} onQuick={adv.changeQuick}
          matches={model.page?.total} onClose={close} onSaveAsView={saveAsView} />
      )}
      <ListToolbar model={model}>
        {canCreate && createKinds.length > 1 && <DesignCreateMenu label={createText} menuLabel={t('modDesign.createKind')} items={createKinds} />}
        <TableActions actions={actions} createText={createText} selected={model.selection.length} canExport={canExport} canCreate={canCreate && createKinds.length <= 1}
          canEdit={canCreate} canDelete={canDelete}
          filtersActive={hasActiveFilters(model.state)} onReset={() => model.setState(RESET_FILTERS)} />
      </ListToolbar>
    </>
  );
}
